/**
 * The About screen's words: what ONDO is, and how to read the code on a work.
 *
 * Every portal links here from its rail, and it answers the question a
 * new holder asks first — what does this registry actually claim about my
 * work, and what does the mark on it mean. There is no model beside this
 * file because the screen decides nothing: `About.tsx` lays the sections
 * out and `CodeLegend.tsx` draws each row with the real renderer from
 * `@ondo/shared`'s `domain/ondoCode.ts`, so the only judgement here is what the copy says.
 *
 * Every sentence is a claim the access rules enforce today. Something the
 * schema cannot yet answer goes in as a row marked not issued, not as a
 * promise phrased in the present tense.
 */

import type { CodeFacts } from "@ondo/shared";

/**
 * What ONDO is, said plainly.
 *
 * Written to match what the schema actually does rather than what a registry
 * might aspire to — every claim here is one the access rules enforce.
 */
/**
 * What ONDO is, from `@ondo/shared`.
 *
 * The copy moved there when the phone app needed the same words — see
 * `domain/about.ts`. Re-exported rather than re-imported at each call site, so
 * `About.tsx` and the legend below are unchanged.
 */
export { ABOUT_SECTIONS } from "@ondo/shared";

/**
 * The ONDO code legend.
 *
 * ONE ROW PER THING THE MARK CAN SAY, and each row's `facts` are fed to the
 * real renderer rather than to a picture of one — so a colour that changes in
 * `ondoCode.ts` changes here, and a band this build cannot draw cannot be drawn
 * here either. A legend that could drift from the mark is worse than no legend,
 * because it is the document somebody reaches for when the mark surprises them.
 *
 * `issued: false` rows are the colours the schema cannot yet answer. They are
 * DOCUMENTED AND NOT DRAWN: the system is explained whole, and the renderer
 * still only ever shows what something decided. Black is buildable and waiting
 * on a screen; orange needs fractional ownership, which is its own piece of
 * work.
 */
export const CODE_LEGEND = {
  title: "The ONDO code",
  lead: "Every work carries a code, issued once and never changed. The bars ARE the code — the same eight characters printed beneath them, drawn — so two works never share a mark and a work never loses its own. What moves is the ink.",
  ownershipTitle: "The state of the work",
  ownershipLead: "One colour, always, and the first of these that is true. It is printed as a word too, so nobody has to tell one colour from another.",
  attributeTitle: "The add-on",
  attributeLead: "Loan, agent and edition are a second, smaller symbol in the same ink, standing off to the right with its own caption — the way a price add-on sits beside an ISBN. They can change without touching the code.",
  stackedTitle: "All of it at once",
  notIssued: "Not yet issued",
  /** A real-shaped code: eight characters, no year, no initials. */
  sample: "OND-K5Q6-3DHR",
} as const;

/** One row of the code legend: a state or attribute, and the facts that draw it. */
export interface LegendEntry {
  key: string;
  name: string;
  note: string;
  facts: CodeFacts | null;
}

/** Band 1, in the order it resolves. */
export const OWNERSHIP: LegendEntry[] = [
  {
    key: "museum",
    name: "Museum",
    note: "An institution owns the work. It outranks Active — once a museum holds title, in the market is what the work no longer is.",
    facts: { sold: true, authorityAttested: true, soldToInstitution: true },
  },
  {
    key: "active",
    name: "Active",
    note: "The registry holds a sale that stands. Most codes are this one: a work already circulating, whoever has vouched for it.",
    facts: { sold: true, authorityAttested: false },
  },
  {
    key: "awaiting",
    name: "Awaiting determination",
    note: "No appointed authority — a studio, estate, foundation or catalogue raisonné archive — has put its name to this record yet. Nothing is being approved; the colour says a signature is absent, not that anyone has refused one.",
    facts: { sold: false, authorityAttested: false },
  },
  {
    key: "never-sold",
    name: "Never sold",
    note: "No sale stands, and an authority has signed. A work arriving from the artist or the estate for the first time.",
    facts: { sold: false, authorityAttested: true },
  },
  {
    key: "historic",
    name: "Historic",
    note: "You held this work and no longer do. Your record of it does not disappear when it changes hands — it goes quiet. Shown only on your own screens, never on a stranger's view of the work.",
    facts: null,
  },
  {
    key: "undisclosed",
    name: "Not disclosed",
    note: "Hairline outlines rather than a colour, and the word reads Not disclosed. This screen is not entitled to say what state the work is in — the catalogue raisonné is the ordinary case, where most works belong to somebody else and their sale state is that owner's business.",
    facts: { sold: null, authorityAttested: null },
  },
];

/** The attribute bands, in the order they stack. */
export const ATTRIBUTES: LegendEntry[] = [
  {
    key: "on-loan",
    name: "On loan to a museum",
    note: "An institution has the work but does not own it. Possession and ownership are different things here, so this rides in the add-on and leaves the colour alone.",
    facts: { sold: false, authorityAttested: true, onLoanToInstitution: true },
  },
  {
    key: "agent",
    name: "Agent",
    note: "Somebody holds the right to sell it on the owner's behalf. The number counts the hands the work has passed through — 1 is direct from the artist.",
    facts: { sold: true, authorityAttested: true, hasAgent: true, agentHands: 2 },
  },
  {
    key: "edition",
    name: "Edition",
    note: "Part of an edition, and the number is which one. Sits on any state above. An artist's proof shows a dash rather than an invented ordinal.",
    facts: { sold: true, authorityAttested: true, editionNumber: 3, editionSize: 7 },
  },
  {
    key: "fractional",
    name: "Fractional",
    note: "Title split across several owners. The registry holds one owner per work today, so this is not a colour any screen can draw yet.",
    facts: null,
  },
];

/** Every band at once, so the reader sees they compose rather than compete. */
export const STACKED: LegendEntry[] = [
  {
    key: "stacked",
    name: "Active · on loan · agented, position 2 · edition 3 of 7",
    note: "One colour for the state, and everything else in the add-on beside it. The bars are identical to every other mark on this page, because they are all the same code — only the ink and the supplement differ.",
    facts: {
      sold: true,
      authorityAttested: true,
      onLoanToInstitution: true,
      hasAgent: true,
      agentHands: 2,
      editionNumber: 3,
      editionSize: 7,
    },
  },
];
